<template>
  <div>
    <portal :to="PostalNames.InvoiceFormPopupOverlay" v-if="isVisible">
      <div
        class="invoice-popup-overlay"
        @click="onClose"
      ></div>
    </portal>
    <b-popover
      :custom-class="customClass"
      placement="auto"
      :ref="popoverRef"
      triggers="click"
      :show.sync="isVisible"
      :target="target"
    >
      <div style="width: 518px; padding: 20px;">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="popup-title">{{ title }}</div>
          <span @click="onClose" class="close-icon cursor-pointer" v-html="closeLgSvg"></span>
        </div>
        <b-table-simple class="invoice-table" :hover="false" stripped responsive>
          <b-thead>
            <b-tr>
              <b-th>Invoice</b-th>
              <b-th class="text-right">Amount, $</b-th>
              <b-th>Status</b-th>
              <b-th>Actions</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="invoice in invoices" :key="invoice.id">
              <b-td>
                <div class="d-flex align-items-center">
                  <span>{{ invoice.invoice_number || invoice.number }}</span>
                </div>
              </b-td>
              <b-td class="text-right">{{ invoice.amount || invoice.amountUSD }}</b-td>
              <b-td>
                <div class="d-flex align-items-center gap-1">
                  <PayoutStatus :status="invoice.status || invoice.invoice_status" />
                  <span
                    v-if="isPaidInDifferentMonth(invoice) || isInvoiceOverdue(invoice)"
                    class="material-symbols-outlined ms-2"
                    :class="isInvoiceOverdue(invoice) ? 'text-danger' : 'text-warning'"
                    style="font-size: 20px;"
                    v-b-tooltip.hover="{
                      title: getEventBusyTooltip(invoice),
                      variant: 'base',
                      container: 'body',
                      placement: 'top',
                      boundary: 'viewport',
                      html: true,
                      delay: 300
                    }"
                  >
                    event_busy
                  </span>
                </div>
              </b-td>
              <b-td>
                <InvoicesLinks
                  :item="invoice"
                  @reloadInvoices="onReloadInvoices"
                  @showEditForm="onShowEditForm"
                />
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>
    </b-popover>
  </div>
</template>

<script>
import PayoutStatus from './tables/elements/PayoutStatus.vue'
import InvoicesLinks from './InvoicesLinks.vue'
import { closeLgSvg } from '@/assets/icons/closeLgIcon.js'
import { PostalNames } from '@/constants/PortalNames'
import moment from 'moment'

export default {
  name: 'InvoicePopup',
  components: {
    PayoutStatus,
    InvoicesLinks
  },
  props: {
    title: {
      type: String,
      required: true
    },
    invoices: {
      type: Array,
      required: true
    },
    target: {
      type: String,
      required: true
    },
    popoverRef: {
      type: String,
      required: true
    },
    customClass: {
      type: String,
      default: ''
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      PostalNames,
      isVisible: this.show,
      closeLgSvg
    }
  },
  watch: {
    show (newValue) {
      this.isVisible = newValue
    },
    isVisible (newValue) {
      this.$emit('update:show', newValue)
    }
  },
  methods: {
    onClose () {
      this.isVisible = false
      this.$emit('close')
    },
    onReloadInvoices () {
      this.$emit('reloadInvoices')
      this.onClose()
    },
    onShowEditForm (invoice) {
      this.$emit('showEditForm', invoice)
    },
    isPaidInDifferentMonth (invoice) {
      if (!invoice.paid_date || !invoice.issue_date || invoice.invoice_status.name !== 'paid') {
        return false
      }

      const issueDate = new Date(invoice.issue_date)
      const paidDate = new Date(invoice.paid_date)

      return issueDate.getFullYear() !== paidDate.getFullYear() ||
             issueDate.getMonth() !== paidDate.getMonth()
    },
    formatDate (date) {
      if (!date) return ''
      return moment(date).format('DD.MM.YYYY')
    },
    isInvoiceOverdue (invoice) {
      if (!invoice.due_date || !invoice.invoice_status) {
        return false
      }

      const isPaid = invoice.invoice_status.name === 'paid' ||
                     (invoice.status && invoice.status.name === 'paid')
      if (isPaid) {
        return false
      }

      const today = new Date()
      const dueDate = new Date(invoice.due_date)
      today.setHours(0, 0, 0, 0)
      dueDate.setHours(0, 0, 0, 0)

      return dueDate < today
    },
    getOverdueDays (invoice) {
      if (!invoice.due_date) return 0

      const today = new Date()
      const dueDate = new Date(invoice.due_date)
      today.setHours(0, 0, 0, 0)
      dueDate.setHours(0, 0, 0, 0)

      const diffTime = today - dueDate
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      return diffDays > 0 ? diffDays : 0
    },
    getEventBusyTooltip (invoice) {
      if (this.isInvoiceOverdue(invoice)) {
        return `This invoice is overdue by ${this.getOverdueDays(invoice)} days.<br>Due date: ${this.formatDate(invoice.due_date)}`
      }
      if (this.isPaidInDifferentMonth(invoice)) {
        return `Invoice settled in a different month than issued<br>Paid at: ${this.formatDate(invoice.paid_date)}`
      }
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>

.popup-title {
  color: $fontColorBase;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
}

.material-symbols-outlined.text-warning {
  color: $colorOrange !important;
}

.material-symbols-outlined.text-danger {
  color: #dc3545 !important;
}

.invoice-table {
  width: 100%;
  max-width: 452px;

  &.table-responsive {
    max-height: 313px;
  }

  thead th {
    color: #7489A9;
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    text-transform: uppercase;
    border: none;
    padding: 0;
    white-space: nowrap;
  }

  tbody tr {
    border-bottom: 1px solid $tableBorderColor;
  }

  tbody td {
    border: none;
    padding: 12px 0;
    color: #3D5170;
  }

  tbody td:nth-child(1),
  tbody td:nth-child(2) {
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.14px;
  }

  th:nth-child(1),
  td:nth-child(1) {
    max-width: 158px;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 26%;
    padding-right: 12px;
  }

  th:nth-child(3),
  td:nth-child(3) {
    max-width: 90px;
    padding-right: 12px;
  }

  th:nth-child(4),
  td:nth-child(4) {
    max-width: 134px;
  }

  tbody td:nth-child(3) {
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;

    .wrap {
      min-width: unset;
      padding: 8px 16px;
      border-radius: 25px;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;

      &.new {
        background: #C3DBFF;
      }

      &.paid {
        background: #C3E6CB;
      }

      &.sent {
        background: #F8E5AA;
      }

      &.expired {
        background: #F8D7DA;
      }
    }
  }

  tbody td:nth-child(4) {
    .btn {
      background: none !important;
      background-color: transparent !important;
      background-image: none !important;
      background-size: auto !important;
    }

    * {
      background-image: none !important;
      background-size: auto !important;
      padding-left: 0;
    }
  }
}

.close-icon.cursor-pointer {
  cursor: pointer;
}

.invoice-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vw;
  opacity: 0.4;
  background: rgba(0, 0, 0, 0.16);
  backdrop-filter: blur(50px);
  z-index: calc(#{$navigationMenuZIndex} - 2);
  pointer-events: all;
}
</style>
